import { ApiError } from '../../../utils/apiClient';

export type SelectionMode = 'chaos' | 'functional';
export type FunctionalSignalState =
  | 'insufficient_signal'
  | 'historical_only'
  | 'runtime_only'
  | 'full';
type LegacySignalState = 'ready';
export type SignalOverride = 'auto' | 'ignore_history' | 'ignore_observability' | 'full';
export type PageState = 'empty' | 'ready' | 'running' | 'result';

export type SelectionSignalReadiness = {
  project_id?: string;
  state: FunctionalSignalState;
  signal_state?: FunctionalSignalState | string;
  has_test_library?: boolean;
  has_sample_library?: boolean;
  has_real_library?: boolean;
  has_experiment_library?: boolean;
  has_historical_signal: boolean;
  has_runtime_signal: boolean;
  test_library_enabled?: boolean;
  historical_data_enabled?: boolean;
  observability_enabled?: boolean;
  runtime_signal_present?: boolean;
  runtime_modulation_applicable?: boolean;
  aggregated_logical_result_count?: number;
  matched_services?: number;
  sample_selection_count?: number;
  sample_selection_limit?: number;
  sample_limit_reached?: boolean;
  signal_override?: string;
  effective_mode?: string;
  runtime_diagnostics?: Record<string, unknown>;
};

export type FunctionalWhyTheseTests = {
  title: string;
  bullets: string[];
  note?: string | null;
  sample_mode?: boolean;
};

export const EXECUTION_STEPS = [
  'Initial Probability Distribution',
  'Applying Superposition',
  'Applying Entanglement',
  'Applying Uncertainty',
  'Final Probability Distribution',
];

export const getStructuredError = (error: unknown) => {
  if (error instanceof ApiError && error.body && typeof error.body.detail === 'object') {
    return error.body.detail as Record<string, unknown>;
  }

  return null;
};

export const inferPrinciple = (
  experiment: any
): 'superposition' | 'entanglement' | 'uncertainty' => {
  const origin = (
    experiment.final_origin ??
    experiment.quantum_origin ??
    experiment.origin ??
    ''
  ).toLowerCase();

  if (origin.includes('uncert')) return 'uncertainty';
  if (origin.includes('entang')) return 'entanglement';
  if (origin.includes('super')) return 'superposition';

  return 'superposition';
};

export const formatPlanUsage = (
  planCode?: string,
  selectionCount?: number | null,
  selectionLimit?: number | null
) => {
  const normalizedPlan =
    planCode?.startsWith('enterprise')
      ? 'Enterprise'
      : `${(planCode || 'unknown').charAt(0).toUpperCase()}${(planCode || 'unknown').slice(1)}`;
  const planLabel = `${normalizedPlan} Plan`;

  if (selectionLimit === null || selectionLimit === undefined) {
    return `${planLabel} · Unlimited selections`;
  }

  return `${planLabel} · ${selectionCount ?? 0} of ${selectionLimit} selections used`;
};

const SIGNAL_STATES: FunctionalSignalState[] = [
  'insufficient_signal',
  'historical_only',
  'runtime_only',
  'full',
];

const isSignalState = (value: unknown): value is FunctionalSignalState =>
  typeof value === 'string' && SIGNAL_STATES.includes(value as FunctionalSignalState);

const hasPositiveCount = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export const normalizeSelectionReadiness = (
  payload: Partial<SelectionSignalReadiness> | null | undefined
): SelectionSignalReadiness | null => {
  if (!payload) {
    return null;
  }

  const signalOverride = String(payload.signal_override || 'auto');
  const observabilityIgnored =
    signalOverride === 'ignore_observability' || payload.observability_enabled === false;
  const legacyReady =
    (payload.state as FunctionalSignalState | LegacySignalState | undefined) === 'ready' ||
    payload.signal_state === 'ready';
  const hasHistorical =
    payload.historical_data_enabled === false
      ? false
      : payload.has_historical_signal === undefined
      ? legacyReady
      : !!payload.has_historical_signal;
  const hasRuntime =
    !observabilityIgnored &&
    (!!payload.has_runtime_signal ||
      !!payload.runtime_signal_present ||
      !!payload.runtime_modulation_applicable ||
      hasPositiveCount(payload.aggregated_logical_result_count) ||
      hasPositiveCount(payload.matched_services));

  let state: FunctionalSignalState =
    isSignalState(payload.state)
      ? payload.state
      : isSignalState(payload.signal_state)
      ? payload.signal_state
      : 'insufficient_signal';

  if (hasHistorical && hasRuntime) {
    state = 'full';
  } else if (hasRuntime) {
    state = 'runtime_only';
  } else if (hasHistorical) {
    state = 'historical_only';
  } else if (!isSignalState(payload.state) && !isSignalState(payload.signal_state)) {
    state = 'insufficient_signal';
  }

  return {
    ...payload,
    state,
    signal_state: state,
    has_historical_signal: hasHistorical,
    has_runtime_signal: hasRuntime,
    test_library_enabled: payload.test_library_enabled !== false,
    historical_data_enabled: payload.historical_data_enabled !== false,
    observability_enabled: payload.observability_enabled !== false,
    signal_override: signalOverride,
    effective_mode: isSignalState(payload.effective_mode) ? payload.effective_mode : state,
  };
};

export const deriveSelectionPageState = ({
  done,
  running,
  signalReadiness,
}: {
  done: boolean;
  running: boolean;
  signalReadiness: SelectionSignalReadiness | null;
}): PageState => {
  if (done) {
    return 'result';
  }

  if (running) {
    return 'running';
  }

  if (signalReadiness?.state === 'insufficient_signal' || signalReadiness?.sample_limit_reached) {
    return 'empty';
  }

  return 'ready';
};
